import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { graphqlFetch, GraphqlFetchOutput } from '@pylot-data/graphqlFetch'
import type {
    AddProductsToCartOutput as AddProductsToCartOutputRaw,
    Cart,
    CartItemInput,
    ProductInterface
} from '@pylot-data/pylotschema'
import { addToCartMutation } from '@components/alfakher/pylot-cart-manager/src/graphql/addToCartMutation'
import { CartData, useCart } from '@corratech/pylot-cart-manager/src/use-cart'
import { getIsSignedIn } from '@corratech/pylot-cart-manager/src/utils/getIsSignedIn'
import { getOptimisticAddToCartData } from '@components/alfakher/pylot-cart-manager/src/utils/getOptimisticAddToCartData'
import { useUI } from '@corratech/pylot-ui/context'

import { CartItemInterface, Maybe } from '@pylot-data/pylotschema'
import { useAlfakherContext } from '@components/alfakherContext/AlfakherContext'

export interface AddProductsToCartInput {
    cartId: string
    cartItems: CartItemInput[]
    isSignedIn: boolean
}

export interface AddProductsToCartOutput {
    addProductsToCart: AddProductsToCartOutputRaw
}

export interface UseAddToCartReturn {
    addToCart: (
        cartItems: CartItemInput[],
        products?: ProductInterface[],
        excludeItems?: string[],
        excludeAlfaBundleItems?: string[]
    ) => Promise<CartData | undefined>
    isAdding: boolean
}

const formatData = (
    output: GraphqlFetchOutput<AddProductsToCartOutput>
): ((oldData?: CartData) => CartData) | CartData => {
    const outputData = output.data.addProductsToCart
    const errors = output.errors

    if (errors?.length || outputData?.user_errors?.length) {
        // populate cart data using the old data and append it with new errors
        return (oldData) => ({
            errors: errors,
            data: oldData?.data ?? outputData,
            user_errors: outputData?.user_errors
        })
    }

    return {
        data: { cart: outputData.cart },
        errors: errors
    }
}

// During 'editing cart item' we remove an old item and add a new item simultaneously
// So, we have to ignore the outdated cart item
const filterExcluded = (
    cart: Cart,
    excludeItems?: string[],
    excludeAlfaBundleItems?: string[]
): Cart => {
    if (cart.items && excludeItems) {
        cart.items = cart.items.filter((item) => {
            if (excludeAlfaBundleItems?.length && item?.sku) {
                return item && !excludeAlfaBundleItems.includes(item.sku)
            }

            return item && !excludeItems.includes(item.uid)
        })
    }
    return cart
}

export const useAddToCart = (): UseAddToCartReturn => {
    const { locale } = useRouter()
    const { data, mutate } = useCart()
    const [isAdding, setIsAdding] = useState(false)
    const { setMinicartLoading } = useUI()
    const [optimisticItems, setOptimisticItems] = useState<
        Maybe<Array<Maybe<CartItemInterface>>>
    >()

    const {
        // @ts-ignore
        setAddToCartPopupPayload,
        // @ts-ignore
        addToCartPopupPayload
    } = useAlfakherContext()

    // If cart data is being updated during ATC request - restore optimistic items
    useEffect(() => {
        if (isAdding && data && optimisticItems) {
            data.data.cart.items = optimisticItems
            mutate(data, false)
        }
    }, [data])

    return {
        addToCart: async (
            cartItems: CartItemInput[],
            products?: ProductInterface[],
            excludeItems?: string[],
            excludeAlfaBundleItems?: string[],
            isDisablePopup = false
        ) => {
            setIsAdding(true)
            setMinicartLoading(true)
            if (products) {
                const optimisticData = getOptimisticAddToCartData(
                    data!,
                    cartItems,
                    products
                )
                optimisticData.data.cart = filterExcluded(
                    optimisticData.data.cart,
                    excludeItems,
                    excludeAlfaBundleItems
                )
                setOptimisticItems(optimisticData.data.cart.items)
                mutate(optimisticData, false)
            }

            // Setting cms block value and fetching the block
            if (products?.length && products[0]?.addtocart_popup_cms) {
                setAddToCartPopupPayload({
                    ...addToCartPopupPayload,
                    cmsBlockId: products[0]?.addtocart_popup_cms
                })
            }

            const response = await graphqlFetch<
                AddProductsToCartInput,
                AddProductsToCartOutput
            >({
                query: addToCartMutation,
                variables: {
                    cartId: String(data!.data.cart.id),
                    cartItems,
                    isSignedIn: getIsSignedIn()
                },
                locale
            })
            setMinicartLoading(false)
            if (response.data.addProductsToCart?.cart?.items) {
                response.data.addProductsToCart.cart = filterExcluded(
                    response.data.addProductsToCart.cart,
                    excludeItems
                )

                // Setting popup to true to show the popup
                if (
                    products?.length &&
                    !response.data.addProductsToCart.user_errors.length &&
                    !isDisablePopup
                ) {
                    setAddToCartPopupPayload({
                        isShowPopup: true,
                        cmsBlockId: products[0]?.addtocart_popup_cms,
                        product: {
                            name: products[0]?.name ?? '',
                            isMultiple: cartItems.length > 1,
                            image: products[0]?.small_image?.url ?? ''
                        },
                        hideContent: addToCartPopupPayload?.isShowPopup
                    })
                }
            } else if (response.errors) {
                setIsAdding(false)
                return mutate(formatData(response), false)
            }

            setOptimisticItems(null)
            const result = await mutate(formatData(response), false)
            // If there were errors - revalidate cart data
            if (
                result?.errors?.length ||
                response.data.addProductsToCart?.user_errors?.length
            ) {
                await mutate(undefined, true)
            }
            setIsAdding(false)
            return result
        },
        isAdding
    }
}
